import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import './TermsPage.scss';

const TermsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const renderList = (key: string) => {
    const items = t(key, { returnObjects: true }) as string[];
    if (!Array.isArray(items)) return null;
    return (
      <ul>
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  };

  return (
    <div className="terms-page">
      <div className="terms-page__container">
        <button type="button" className="page-back" onClick={() => navigate('/')} aria-label={t('nav.back')}>
          ← {t('nav.back')}
        </button>
        <h1>{t('terms.title')}</h1>
        <p className="terms-page__updated">{t('terms.lastUpdated')}</p>

        <nav className="terms-page__nav" aria-label={t('terms.nav.title')}>
          <h2 className="terms-page__nav-title">{t('terms.nav.title')}</h2>
          <ul>
            <li><a href="#privacy">{t('terms.nav.privacy')}</a></li>
            <li><a href="#ai-disclaimer">{t('terms.nav.aiDisclaimer')}</a></li>
            <li><a href="#liability">{t('terms.nav.liability')}</a></li>
            <li><a href="#refund">{t('terms.nav.refund')}</a></li>
            <li><a href="#changes">{t('terms.nav.changes')}</a></li>
          </ul>
        </nav>

        <article>
          <section id="privacy" className="terms-page__section">
            <h2>{t('terms.privacy.title')}</h2>

            <h3>{t('terms.privacy.dataController.title')}</h3>
            <p>{t('terms.privacy.dataController.intro')}</p>
            <p><strong>{t('terms.privacy.dataController.operator')}</strong></p>
            <p>{t('terms.privacy.dataController.address')}</p>
            <p>Email: <a href={`mailto:${t('terms.privacy.dataController.email')}`}>{t('terms.privacy.dataController.email')}</a></p>
            <p>{t('terms.privacy.dataController.support')}: <a href={`mailto:${t('terms.privacy.dataController.supportEmail')}`}>{t('terms.privacy.dataController.supportEmail')}</a></p>
            <p>{t('terms.privacy.dataController.gdprNote')}</p>

            <h3>{t('terms.privacy.dataCollected.title')}</h3>
            <p>{t('terms.privacy.dataCollected.intro')}</p>
            <h4>{t('terms.privacy.dataCollected.accountTitle')}</h4>
            {renderList('terms.privacy.dataCollected.accountItems')}
            <h4>{t('terms.privacy.dataCollected.profileTitle')}</h4>
            {renderList('terms.privacy.dataCollected.profileItems')}
            <h4>{t('terms.privacy.dataCollected.paymentTitle')}</h4>
            <p><em>{t('terms.privacy.dataCollected.paymentNote')}</em></p>
            <h4>{t('terms.privacy.dataCollected.usageTitle')}</h4>
            {renderList('terms.privacy.dataCollected.usageItems')}

            <h3>{t('terms.privacy.purpose.title')}</h3>
            <p>{t('terms.privacy.purpose.intro')}</p>
            {renderList('terms.privacy.purpose.items')}

            <h3>{t('terms.privacy.legalBasis.title')}</h3>
            <p>{t('terms.privacy.legalBasis.intro')}</p>
            {renderList('terms.privacy.legalBasis.items')}

            <h3>{t('terms.privacy.thirdParty.title')}</h3>
            <p>{t('terms.privacy.thirdParty.intro')}</p>
            <ul>
              <li><strong>{t('terms.privacy.thirdParty.paymentLabel')}</strong> {t('terms.privacy.thirdParty.payment')}</li>
              <li><strong>{t('terms.privacy.thirdParty.aiLabel')}</strong> {t('terms.privacy.thirdParty.ai')}</li>
              <li><strong>{t('terms.privacy.thirdParty.hostingLabel')}</strong> {t('terms.privacy.thirdParty.hosting')}</li>
              <li><strong>{t('terms.privacy.thirdParty.communicationLabel')}</strong> {t('terms.privacy.thirdParty.communication')}</li>
            </ul>
            <p>{t('terms.privacy.thirdParty.note')}</p>

            <h3>{t('terms.privacy.retention.title')}</h3>
            <p>{t('terms.privacy.retention.intro')}</p>
            <ul>
              <li>{t('terms.privacy.retention.account')}</li>
              <li>{t('terms.privacy.retention.chat')}</li>
              <li>{t('terms.privacy.retention.payment')}</li>
            </ul>
            <p>{t('terms.privacy.retention.deletion')}</p>

            <h3>{t('terms.privacy.rights.title')}</h3>
            <p>{t('terms.privacy.rights.intro')}</p>
            {renderList('terms.privacy.rights.items')}
            <p>{t('terms.privacy.rights.contact')} <a href={`mailto:${t('terms.privacy.dataController.email')}`}>{t('terms.privacy.dataController.email')}</a></p>

            <h3>{t('terms.privacy.security.title')}</h3>
            <p>{t('terms.privacy.security.intro')}</p>
            {renderList('terms.privacy.security.items')}

            <h3>{t('terms.privacy.cookies.title')}</h3>
            <p>{t('terms.privacy.cookies.intro')}</p>
            {renderList('terms.privacy.cookies.items')}
            <p>{t('terms.privacy.cookies.note')}</p>
          </section>

          <section id="ai-disclaimer" className="terms-page__section">
            <h2>{t('terms.aiDisclaimer.title')}</h2>
            <p>{t('terms.aiDisclaimer.intro')}</p>
            <p>{t('terms.aiDisclaimer.serviceLabel')}</p>
            {renderList('terms.aiDisclaimer.points')}
            <p><strong>{t('terms.aiDisclaimer.responsibility')}</strong></p>
          </section>

          <section id="liability" className="terms-page__section">
            <h2>{t('terms.liability.title')}</h2>
            <p>{t('terms.liability.intro')}</p>
            {renderList('terms.liability.items')}
            <p><strong>{t('terms.liability.maxLiability')}</strong></p>
          </section>

          <section id="refund" className="terms-page__section">
            <h2>{t('terms.refund.title')}</h2>
            <p>{t('terms.refund.billing')}</p>
            <p>{t('terms.refund.noRefund')}</p>
            <p>{t('terms.refund.access')}</p>
          </section>

          <section id="changes" className="terms-page__section">
            <h2>{t('terms.changes.title')}</h2>
            <p>{t('terms.changes.intro')}</p>
            <p>{t('terms.changes.notification')}</p>
          </section>
        </article>

        <div className="terms-page__footer">
          <Link to="/" className="terms-page__link">{t('terms.backToHome')}</Link>
          <Link to="/terms-of-service" className="terms-page__link">{t('home.footer.termsOfService')}</Link>
          <button type="button" className="terms-page__link terms-page__link--btn" onClick={() => window.dispatchEvent(new CustomEvent('numerolife:openCookiePreferences'))}>
            {t('home.footer.cookies')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
